import { useTranslation } from 'react-i18next'
import * as React from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Layers,
  Server,
  Database,
  Activity,
  Search,
  Package,
  SearchX,
} from 'lucide-react'

export type EmptyStateVariant = 'empty' | 'filtered'

export type EmptyStateIllustrationKind =
  | 'models'
  | 'deployments'
  | 'datasets'
  | 'training'
  | 'search'
  | 'generic'

type EmptyStateProps = {
  variant?: EmptyStateVariant
  illustration?: EmptyStateIllustrationKind
  title?: string
  description?: string
  action?: React.ReactNode
  secondaryAction?: React.ReactNode
  onClearFilters?: () => void
  className?: string
}

function Illustration({ kind }: { kind: EmptyStateIllustrationKind }) {
  const iconClass = 'h-7 w-7'
  let node: React.ReactNode
  switch (kind) {
    case 'models':
      node = <Layers className={iconClass} aria-hidden="true" />
      break
    case 'deployments':
      node = <Server className={iconClass} aria-hidden="true" />
      break
    case 'datasets':
      node = <Database className={iconClass} aria-hidden="true" />
      break
    case 'training':
      node = <Activity className={iconClass} aria-hidden="true" />
      break
    case 'search':
      node = <SearchX className={iconClass} aria-hidden="true" />
      break
    default:
      node = <Package className={iconClass} aria-hidden="true" />
      break
  }
  return (
    <div
      aria-hidden="true"
      className="flex h-14 w-14 items-center justify-center rounded-none border border-enterprise-border bg-enterprise-surface font-mono text-enterprise-muted shadow-none"
    >
      {node}
    </div>
  )
}

function FilteredIllustration() {
  return (
    <div
      aria-hidden="true"
      className="flex h-14 w-14 items-center justify-center rounded-none border border-enterprise-border bg-enterprise-surface font-mono text-enterprise-muted shadow-none"
    >
      <span className="relative flex items-center justify-center">
        <Search className="h-6 w-6" aria-hidden="true" />
        <SearchX className="absolute -right-1 -top-1 h-3.5 w-3.5 rounded-full bg-enterprise-surface font-mono text-enterprise-muted" aria-hidden="true" />
      </span>
    </div>
  )
}

export function EmptyState({
  variant = 'empty',
  illustration = 'generic',
  title,
  description,
  action,
  secondaryAction,
  onClearFilters,
  className,
}: EmptyStateProps) {
  const { t } = useTranslation()
  const resolvedClearLabel = t('common.clearFilters', { defaultValue: 'Clear filters' })
  const isFiltered = variant === 'filtered'

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex min-h-[188px] flex-col items-center justify-center gap-3 rounded-none border border-dashed border-enterprise-border bg-enterprise-bg px-6 py-8 text-center shadow-none',
        className,
      )}
    >
      <div aria-hidden="true" className="flex flex-col items-center">
        {isFiltered ? <FilteredIllustration /> : <Illustration kind={illustration} />}
      </div>

      <div className="space-y-1.5">
        {title ? <h3 className="text-sm font-semibold leading-none tracking-tight text-enterprise-text text-balance">{title}</h3> : null}
        {description ? (
          <p className="mx-auto max-w-[60ch] text-sm leading-relaxed text-enterprise-muted text-balance">{description}</p>
        ) : null}
      </div>

      <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
        {action ? <div className="flex min-h-11 items-center justify-center">{action}</div> : null}
        {isFiltered && onClearFilters ? (
          <Button
            type="button"
            variant="outline"
            size="default"
            onClick={onClearFilters}
            className="min-h-11 rounded-full border-enterprise-border bg-enterprise-surface px-4 text-sm font-medium text-enterprise-text hover:bg-enterprise-raised hover:text-enterprise-text"
          >
            {resolvedClearLabel}
          </Button>
        ) : null}
        {secondaryAction ? <div className="flex min-h-11 items-center justify-center">{secondaryAction}</div> : null}
      </div>

      {isFiltered && !onClearFilters && !secondaryAction && !action ? (
        <p className="text-sm text-enterprise-muted">{t('common.tryAdjustFilters', { defaultValue: 'Try adjusting filters or clear them and retry.' })}</p>
      ) : null}
    </div>
  )
}
